import { useCallback, useEffect, useRef, useState } from 'react';

import type { ReviewerContextFlags, ReviewerList, ReviewerRow } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { registerUnsavedEditor } from '../lib/unsaved.ts';
import { useSettingsAutosave } from '../lib/settings-autosave.tsx';
import {
  canWriteReviewers,
  newReviewerDraft,
  REVIEWER_FLAG_COPY,
  REVIEWER_ROUTES_NOTE,
  reviewerFlagKeys,
  toggleOptionalFlag,
} from '../lib/reviewers.ts';
import { api, type SessionUser } from '../live/api.ts';
import { useStore } from '../live/context.tsx';

export function ReviewersPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const canWrite = canWriteReviewers(user);
  const [page, setPage] = useState<ReviewerList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setPage(await api.reviewers());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !page) {
    return (
      <Empty
        title="Reviewers could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="reviewers-panel">
      <p className="sheet-note cfg-provenance">
        Required reviewers on a verification contract run in parallel at AI Review. Each has a
        prompt and flags for what context the runner materialises. Ticket context is always on.
        Full checkout at the MR head SHA and named CI artifacts are off unless you turn them on.
        {' '}{REVIEWER_ROUTES_NOTE}
        {canWrite ? null : ' Only an admin can change reviewers.'}
      </p>
      {page.items.map((row) => (
        <ReviewerEditor
          key={row.id}
          row={row}
          canWrite={canWrite}
          onSaved={load}
        />
      ))}
      {canWrite ? (
        creating ? (
          <ReviewerCreateForm
            onCancel={() => setCreating(false)}
            onCreated={async (name) => {
              setCreating(false);
              store.toast('good', `${name} saved.`);
              await load();
            }}
            onError={(message) => store.toast('bad', message)}
          />
        ) : (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            Add a reviewer
          </button>
        )
      ) : null}
    </div>
  );
}

function ReviewerEditor({
  row,
  canWrite,
  onSaved,
}: {
  row: ReviewerRow;
  canWrite: boolean;
  onSaved: (message: string) => Promise<void>;
}) {
  const initial = {
    name: row.name,
    prompt: row.prompt,
    flags: row.contextFlags,
    vendorExclusion: row.vendorExclusion,
    enabled: row.enabled,
  };
  const persist = useCallback(async (draft: typeof initial) => {
    await api.updateReviewer(row.id, {
      name: draft.name,
      prompt: draft.prompt,
      contextFlags: draft.flags,
      vendorExclusion: draft.vendorExclusion,
      enabled: draft.enabled,
    });
    await onSaved('saved');
  }, [onSaved, row.id]);
  const autosave = useSettingsAutosave({
    value: initial,
    save: persist,
    what: `the reviewer ${row.name}`,
    enabled: canWrite,
    delay: 700,
    validate: (draft) => !draft.name.trim() || !draft.prompt.trim() ? 'Name and prompt are required.' : null,
  });
  const { draft, setDraft, status, error } = autosave;
  const { name, prompt, flags, vendorExclusion, enabled } = draft;

  return (
    <section className="sheet-panel" data-reviewer={row.name}>
      <header className="panel-head">
        <Kicker>{row.name}</Kicker>
        <span className="mono dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          {enabled ? 'enabled' : 'disabled'}
        </span>
      </header>
      <div className="sheet-panel-body">
        <ReviewerFields
          name={name}
          prompt={prompt}
          flags={flags}
          vendorExclusion={vendorExclusion}
          enabled={enabled}
          canWrite={canWrite}
          onName={(value) => setDraft({ ...draft, name: value })}
          onPrompt={(value) => setDraft({ ...draft, prompt: value })}
          onFlags={(value) => setDraft({ ...draft, flags: value })}
          onVendorExclusion={(value) => setDraft({ ...draft, vendorExclusion: value })}
          onEnabled={(value) => setDraft({ ...draft, enabled: value })}
        />
        {status === 'saving' ? <p className="cfg-help">Saving…</p> : null}
        {error ? <p className="cfg-error" role="alert">Unsaved: {error}</p> : null}
      </div>
    </section>
  );
}

function ReviewerCreateForm({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: (name: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const draft = newReviewerDraft();
  const [name, setName] = useState(draft.name);
  const [prompt, setPrompt] = useState(draft.prompt);
  const [flags, setFlags] = useState(draft.contextFlags);
  const [vendorExclusion, setVendorExclusion] = useState(draft.vendorExclusion);
  const [enabled, setEnabled] = useState(draft.enabled);
  const [busy, setBusy] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = name.trim().length > 0 || prompt.trim().length > 0;
  useEffect(() => registerUnsavedEditor({ isDirty: () => dirtyRef.current, what: 'the new reviewer' }), []);

  const save = async () => {
    setBusy(true);
    try {
      const created = await api.createReviewer({
        name,
        prompt,
        contextFlags: flags,
        vendorExclusion,
        enabled,
      });
      await onCreated(created.name);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sheet-panel" data-reviewer="new">
      <header className="panel-head">
        <Kicker>New reviewer</Kicker>
      </header>
      <div className="sheet-panel-body">
        <ReviewerFields
          name={name}
          prompt={prompt}
          flags={flags}
          vendorExclusion={vendorExclusion}
          enabled={enabled}
          canWrite
          onName={setName}
          onPrompt={setPrompt}
          onFlags={setFlags}
          onVendorExclusion={setVendorExclusion}
          onEnabled={setEnabled}
        />
        <div className="reviewers-actions">
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Create'}
          </button>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}

function ReviewerFields({
  name,
  prompt,
  flags,
  vendorExclusion,
  enabled,
  canWrite,
  onName,
  onPrompt,
  onFlags,
  onVendorExclusion,
  onEnabled,
}: {
  name: string;
  prompt: string;
  flags: ReviewerContextFlags;
  vendorExclusion: boolean;
  enabled: boolean;
  canWrite: boolean;
  onName: (value: string) => void;
  onPrompt: (value: string) => void;
  onFlags: (value: ReviewerContextFlags) => void;
  onVendorExclusion: (value: boolean) => void;
  onEnabled: (value: boolean) => void;
}) {
  return (
    <div className="reviewers-fields">
      <label className="reviewers-field">
        <span className="cfg-label">Name</span>
        <input
          className="input"
          value={name}
          disabled={!canWrite}
          onChange={(event) => onName(event.target.value)}
        />
      </label>
      <div className="cfg-field">
        <div style={{ minWidth: 0 }}>
          <span className="cfg-label">Enabled</span>
          <p className="cfg-help">Disabled reviewers are not dispatched at AI Review.</p>
        </div>
        <div className="cfg-control">
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canWrite}
            aria-pressed={enabled}
            onClick={() => onEnabled(!enabled)}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>
      <div className="cfg-field">
        <div style={{ minWidth: 0 }}>
          <span className="cfg-label">Vendor exclusion</span>
          <p className="cfg-help">
            Prefer a different vendor than the implementer. On by default for Code Review.
          </p>
        </div>
        <div className="cfg-control">
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canWrite}
            aria-pressed={vendorExclusion}
            onClick={() => onVendorExclusion(!vendorExclusion)}
          >
            {vendorExclusion ? 'On' : 'Off'}
          </button>
        </div>
      </div>
      {reviewerFlagKeys().map((key) => {
        const copy = REVIEWER_FLAG_COPY[key];
        const on = flags[key];
        const locked = copy.locked;
        return (
          <div className="cfg-field" key={key}>
            <div style={{ minWidth: 0 }}>
              <span className="cfg-label">{copy.label}</span>
              <p className="cfg-help">{copy.help}</p>
            </div>
            <div className="cfg-control">
              <button
                type="button"
                className="btn btn-sm"
                disabled={!canWrite || locked}
                aria-pressed={on}
                onClick={() => {
                  if (key === 'ticketContext') return;
                  onFlags(toggleOptionalFlag(flags, key));
                }}
              >
                {on ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        );
      })}
      <label className="reviewers-field">
        <span className="cfg-label">Prompt</span>
        <p className="cfg-help">
          Per-reviewer instructions. The stage pack at <span className="mono">ai-review</span> is
          the agent CLI protocol; this text is what this reviewer is asked to do.
        </p>
        <textarea
          className="input reviewers-prompt"
          value={prompt}
          disabled={!canWrite}
          rows={8}
          spellCheck={false}
          onChange={(event) => onPrompt(event.target.value)}
        />
      </label>
    </div>
  );
}
